import { useEffect, useState } from "react";
import { format } from "date-fns";
import Parse, { ParseClasses } from "../api/config";
import Modal from "./Modal";

export const JobApplicationsManager = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Fetch job applications when component mounts
    useEffect(() => {
        fetchApplications();
    }, []);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const JobApplication = Parse.Object.extend(ParseClasses.JobApplication);
            const query = new Parse.Query(JobApplication);
            query.descending('createdAt');
            query.limit(100);

            const results = await query.find();
            const apps = results.map(app => ({
                id: app.id,
                personalInfo: app.get('personalInfo') || {},
                employers: app.get('employers') || [],
                spiritualLife: app.get('spiritualLife') || {},
                education: app.get('education') || {},
                references: app.get('references') || [],
                professionalAssociations: app.get('professionalAssociations') || [],
                employmentComments: app.get('employmentComments') || '',
                certificationAgreed: app.get('certificationAgreed'),
                signatureDate: app.get('signatureDate'),
                submittedAt: app.get('submittedAt'),
                status: app.get('status') || 'pending',
                createdAt: app.createdAt
            }));
            setApplications(apps);
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewApplication = (application) => {
        setSelectedApplication(application);
        setShowViewModal(true);
    };

    const handleExportPDF = async (application) => {
        try {
            setExporting(true);

            // Call cloud function to generate PDF
            const result = await Parse.Cloud.run('exportJobApplicationPDF', {
                applicationId: application.id
            });

            if (result.success && result.pdfData) {
                // Convert base64 to blob and download
                const byteCharacters = atob(result.pdfData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });

                // Create download link
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = result.filename || `Application_${application.personalInfo.name?.replace(/\s+/g, '_')}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the object URL
                URL.revokeObjectURL(link.href);
            } else {
                alert('Error generating PDF. Please try again.');
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Error exporting PDF. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const updateApplicationStatus = async (applicationId, newStatus) => {
        try {
            const JobApplication = Parse.Object.extend(ParseClasses.JobApplication);
            const query = new Parse.Query(JobApplication);
            const application = await query.get(applicationId);

            application.set('status', newStatus);
            await application.save();

            // Refresh the list
            fetchApplications();
        } catch (error) {
            console.error('Error updating application status:', error);
            alert('Error updating status. Please try again.');
        }
    };

    const ApplicationViewModal = () => {
        if (!selectedApplication) return null;

        const app = selectedApplication;

        return (
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedApplication(null);
                }}
                title="Job Application Details"
                size="xl"
            >
                <div className="max-h-[70vh] overflow-y-auto">
                    {/* Personal Information */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Personal Information</h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <span className="font-medium">Name:</span> {app.personalInfo.name}
                            </div>
                            <div>
                                <span className="font-medium">Email:</span> {app.personalInfo.email}
                            </div>
                            <div>
                                <span className="font-medium">Phone:</span> {app.personalInfo.telephone}
                            </div>
                            <div>
                                <span className="font-medium">Position:</span> {app.personalInfo.positionAppliedFor}
                            </div>
                            <div>
                                <span className="font-medium">Address:</span> {app.personalInfo.address}
                            </div>
                            <div>
                                <span className="font-medium">Pay Expected:</span> {app.personalInfo.payExpected}
                            </div>
                            <div>
                                <span className="font-medium">Available Date:</span> {app.personalInfo.dateAvailable}
                            </div>
                            <div>
                                <span className="font-medium">Legally Eligible:</span> {app.personalInfo.legallyEligible}
                            </div>
                        </div>
                    </div>

                    {/* Employment History */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Employment History</h3>
                        {app.employers.filter(emp => emp.employer).map((emp, index) => (
                            <div key={index} className="mb-4 p-3 bg-gray-50 rounded">
                                <div className="font-medium mb-2">Employer #{index + 1}: {emp.employer}</div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="font-medium">Phone:</span> {emp.phone}</div>
                                    <div><span className="font-medium">Supervisor:</span> {emp.supervisor}</div>
                                    <div><span className="font-medium">From:</span> {emp.datesFrom}</div>
                                    <div><span className="font-medium">To:</span> {emp.datesTo}</div>
                                    <div className="col-span-2">
                                        <span className="font-medium">Work Performed:</span> {emp.workPerformed}
                                    </div>
                                    <div className="col-span-2">
                                        <span className="font-medium">Reason for Leaving:</span> {emp.reasonForLeaving}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {app.employmentComments && (
                            <div className="mt-3">
                                <span className="font-medium">Comments:</span> {app.employmentComments}
                            </div>
                        )}
                    </div>

                    {/* Spiritual Life */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Spiritual Life</h3>
                        <div className="grid grid-cols-1 gap-2 text-sm">
                            <div>
                                <span className="font-medium">Date and Place of New Birth:</span> {app.spiritualLife.dateAndPlaceOfNewBirth}
                            </div>
                            <div>
                                <span className="font-medium">Church Attending:</span> {app.spiritualLife.churchAttending}
                            </div>
                            <div>
                                <span className="font-medium">Denomination:</span> {app.spiritualLife.denomination}
                            </div>
                            <div>
                                <span className="font-medium">Pastor's Name:</span> {app.spiritualLife.pastorsName}
                            </div>
                            <div>
                                <span className="font-medium">Church Involvement:</span> {app.spiritualLife.churchInvolvement}
                            </div>
                            <div>
                                <span className="font-medium">Baptism of Holy Spirit:</span> {app.spiritualLife.baptismHolySpirit}
                            </div>
                        </div>
                    </div>

                    {/* Education */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Education</h3>
                        {app.education && Object.entries(app.education).map(([level, edu]) => {
                            if (!edu.name) return null;
                            return (
                                <div key={level} className="mb-3">
                                    <div className="font-medium mb-1">
                                        {level === 'highSchool' && 'High School'}
                                        {level === 'tradeSchool' && 'Trade School'}
                                        {level === 'college' && 'College'}
                                        {level === 'graduateSchool' && 'Graduate School'}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm pl-4">
                                        <div><span className="font-medium">Name:</span> {edu.name}</div>
                                        <div><span className="font-medium">Major:</span> {edu.major}</div>
                                        <div><span className="font-medium">Years:</span> {edu.yearsCompleted}</div>
                                        <div><span className="font-medium">Degree:</span> {edu.diplomaDegree}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* References */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">References</h3>
                        {app.references.filter(ref => ref.name).map((ref, index) => (
                            <div key={index} className="mb-3 p-3 bg-gray-50 rounded">
                                <div className="font-medium mb-2">Reference #{index + 1}: {ref.name}</div>
                                <div className="text-sm">
                                    <div>{ref.street}, {ref.city}, {ref.state} {ref.zip}</div>
                                    <div>Email: {ref.email} | Phone: {ref.telephone}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Professional Associations */}
                    {app.professionalAssociations.some(assoc => assoc.organization) && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Professional Associations</h3>
                            {app.professionalAssociations.filter(assoc => assoc.organization).map((assoc, index) => (
                                <div key={index} className="mb-2 text-sm">
                                    <span className="font-medium">{assoc.organization}</span>
                                    {assoc.officeHeld && ` - ${assoc.officeHeld}`}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Certification */}
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 border-b pb-2">Certification</h3>
                        <div className="text-sm">
                            <div>
                                <span className="font-medium">Agreement:</span> {app.certificationAgreed ? 'Yes' : 'No'}
                            </div>
                            <div>
                                <span className="font-medium">Signature Date:</span> {app.signatureDate}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                        <button
                            onClick={() => handleExportPDF(app)}
                            disabled={exporting}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {exporting ? 'Exporting...' : 'Export PDF'}
                        </button>
                        <button
                            onClick={() => {
                                setShowViewModal(false);
                                setSelectedApplication(null);
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Job Applications</h2>
                <p className="text-sm text-gray-600 mt-1">
                    Manage and review job applications submitted through the public form
                </p>
            </div>

            {applications.length === 0 ? (
                <div className="bg-white shadow rounded-lg p-6 text-center">
                    <p className="text-gray-500">No job applications have been submitted yet.</p>
                </div>
            ) : (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Applicant
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Position
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Submitted
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {applications.map((app) => (
                                <tr key={app.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {app.personalInfo.name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {app.personalInfo.email}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {app.personalInfo.positionAppliedFor}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {app.createdAt ? format(new Date(app.createdAt), 'MMM dd, yyyy') : 'N/A'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <select
                                            value={app.status}
                                            onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                                            className="text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="reviewing">Reviewing</option>
                                            <option value="interviewed">Interviewed</option>
                                            <option value="accepted">Accepted</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button
                                            onClick={() => handleViewApplication(app)}
                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                        >
                                            View
                                        </button>
                                        <button
                                            onClick={() => handleExportPDF(app)}
                                            disabled={exporting}
                                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                        >
                                            Export PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showViewModal && <ApplicationViewModal />}
        </div>
    );
};